import { ListResponseModel, toQueryString } from ".";
import { AggregatedTransactions } from "../model/aggregated-transactions";
import { CustomerQuery } from "../model/customer/customer";
import {
	TransactionParams,
	CustomerTransactionParams,
	Transaction,
	TransactionUtilityAdminParams,
	TransactionReference,
	UtilityDashboardSummary,
	UtilityDashboardSummaryParams,
	AggregatedTransactionsRequest,
	TransactionItem,
	DistrictRevenueData,
	DistrictRevenueParams,
	RefundTransaction,
} from "../model/transaction";
import { apiSlice } from "../stores/base/api-slice";

const serviceBaseurl = "/transactions";

const transactionEndpoint = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getPostpaidTransactions: builder.query<ListResponseModel<Array<Transaction>>, TransactionParams>({
			providesTags: ["Transactions"],
			query: params => ({
				url: `${serviceBaseurl}/admin/postpaid` + toQueryString(params),
				method: "GET",
			}),
		}),
		getTransactionsForUtilityAdmin: builder.query<ListResponseModel<Array<Transaction>>, CustomerQuery>({
			query: query => ({
				url: `${serviceBaseurl}/for-utility-admin/${query.userId}`,
				method: "GET",
				params: query,
			}),
		}),
		getPrepaidTransactions: builder.query<ListResponseModel<Array<Transaction>>, TransactionParams>({
			providesTags: ["Transactions"],
			query: params => ({
				url: `${serviceBaseurl}/admin/prepaid` + toQueryString(params),
				method: "GET",
			}),
		}),
		downloadTransactionInvoice: builder.query<string, string>({
			query: transactionReference => ({
				url: `/pdf/download-invoice/${transactionReference}`,
				method: "GET",
			}),
		}),
		getPrepaidTransactionsForUtilityAdmin: builder.query<ListResponseModel<Array<Transaction>>, TransactionUtilityAdminParams>({
			providesTags: ["Transactions"],
			query: params => ({
				url: `${serviceBaseurl}/utility-admin/prepaid` + toQueryString(params),
				method: "GET",
			}),
		}),
		getPrepaidTransactionByReferenceForUtilityAdmin: builder.query<Transaction, TransactionReference>({
			providesTags: ["Transaction"],
			query: params => ({
				url: `${serviceBaseurl}/utility-admin/prepaid/reference` + toQueryString(params),
				method: "GET",
			}),
		}),

		getPostpaidTransactionsForUtilityAdmin: builder.query<ListResponseModel<Array<Transaction>>, TransactionUtilityAdminParams>({
			providesTags: ["Transactions"],
			query: params => ({
				url: `${serviceBaseurl}/utility-admin/postpaid` + toQueryString(params),
				method: "GET",
			}),
		}),
		exportPrepaidTransactions: builder.query<Blob, TransactionParams>({
			query: params => ({
				url: `${serviceBaseurl}/admin/prepaid/export` + toQueryString(params),
				method: "GET",
				responseHandler: async (response: Response) => response.blob(),
			}),
		}),
		exportPrepaidTransactionsForUtilityAdmin: builder.query<Blob, TransactionUtilityAdminParams>({
			query: params => ({
				url: `${serviceBaseurl}/utility-admin/prepaid/export` + toQueryString(params),
				method: "GET",
				responseHandler: async (response: Response) => response.blob(),
			}),
		}),
		exportPostpaidTransactionsForUtilityAdmin: builder.query<Blob, TransactionUtilityAdminParams>({
			query: params => ({
				url: `${serviceBaseurl}/utility-admin/postpaid/export` + toQueryString(params),
				method: "GET",
				responseHandler: async (response: Response) => response.blob(),
			}),
		}),
		getPostpaidTransactionByReferenceForUtilityAdmin: builder.query<Transaction, TransactionReference>({
			providesTags: ["Transaction"],
			query: params => ({
				url: `${serviceBaseurl}/utility-admin/postpaid/reference` + toQueryString(params),
				method: "GET",
			}),
		}),
		getPrepaidTransactionByReferenceForCustomer: builder.query<Transaction, TransactionReference>({
			query: params => ({
				url: `${serviceBaseurl}/prepaid/reference` + toQueryString(params),
				method: "GET",
			}),
		}),
		getPostpaidTransactionByReferenceForCustomer: builder.query<Transaction, TransactionReference>({
			query: params => ({
				url: `${serviceBaseurl}/postpaid/reference` + toQueryString(params),
				method: "GET",
			}),
		}),
		getOrderTransactionByReferenceForCustomer: builder.query<Transaction, string>({
			query: reference => ({
				url: `${serviceBaseurl}/order-transaction/${reference}`,
				method: "GET",
			}),
		}),
		getInstallmentTransactionByReferenceForCustomer: builder.query<Transaction, string>({
			query: reference => ({
				url: `${serviceBaseurl}/installment-transaction/${reference}`,
				method: "GET",
			}),
		}),
		getPayAsYouGoTransactionByReferenceForCustomer: builder.query<Transaction, TransactionReference>({
			query: params => ({
				url: `${serviceBaseurl}/pay-as-you-go/reference` + toQueryString(params),
				method: "GET",
			}),
		}),
		getBulkPurchaseTransactionByReferenceForCustomer: builder.query<Transaction, string>({
			query: reference => ({
				url: `${serviceBaseurl}/bulk-purchase-transaction/${reference}`,
				method: "GET",
			}),
		}),
		getServiceRequestTransactionByReferenceForCustomer: builder.query<Transaction, string>({
			query: reference => ({
				url: `${serviceBaseurl}/service-request-transaction/${reference}`,
				method: "GET",
			}),
		}),
		getRefundTransactionByReferenceForCustomer: builder.query<RefundTransaction, TransactionReference>({
			query: params => ({
				url: `/charge/customer/transactions/refund/reference` + toQueryString(params),
				method: "GET",
			}),
		}),
		getCustomerTransactions: builder.query<ListResponseModel<Array<TransactionItem>>, CustomerTransactionParams>({
			query: params => ({
				url: `${serviceBaseurl}` + toQueryString(params),
				method: "GET",
			}),
		}),
		getUtilityDashboardSummary: builder.query<UtilityDashboardSummary, UtilityDashboardSummaryParams>({
			query: params => ({
				url: `/dashboard/utility/summary` + toQueryString(params),
				method: "GET",
			}),
		}),
		getAggregatedTransactions: builder.query<Array<AggregatedTransactions>, AggregatedTransactionsRequest>({
			query: params => ({
				url: `${serviceBaseurl}/aggregated-transactions` + toQueryString(params),
				method: "GET",
			}),
		}),
		getDistrictRevenue: builder.query<DistrictRevenueData, DistrictRevenueParams>({
			query: params => ({
				url: `/dashboard/utility/district-revenue` + toQueryString(params),
				method: "GET",
			}),
		}),
	}),
});

export const {
	useLazyGetPostpaidTransactionsQuery,
	useLazyGetCustomerTransactionsQuery,
	useLazyGetPrepaidTransactionsQuery,
	useLazyGetTransactionsForUtilityAdminQuery,
	useLazyDownloadTransactionInvoiceQuery,
	useLazyGetPrepaidTransactionsForUtilityAdminQuery,
	useLazyGetPrepaidTransactionByReferenceForUtilityAdminQuery,
	useGetPrepaidTransactionByReferenceForUtilityAdminQuery,
	useGetPostpaidTransactionByReferenceForUtilityAdminQuery,
	useLazyGetPostpaidTransactionsForUtilityAdminQuery,
	useLazyExportPrepaidTransactionsQuery,
	useLazyExportPrepaidTransactionsForUtilityAdminQuery,
	useLazyExportPostpaidTransactionsForUtilityAdminQuery,
	useLazyGetPostpaidTransactionByReferenceForUtilityAdminQuery,
	useLazyGetPrepaidTransactionByReferenceForCustomerQuery,
	useGetPrepaidTransactionByReferenceForCustomerQuery,
	useLazyGetPostpaidTransactionByReferenceForCustomerQuery,
	useGetPostpaidTransactionByReferenceForCustomerQuery,
	useGetOrderTransactionByReferenceForCustomerQuery,
	useGetInstallmentTransactionByReferenceForCustomerQuery,
	useGetPayAsYouGoTransactionByReferenceForCustomerQuery,
	useGetBulkPurchaseTransactionByReferenceForCustomerQuery,
	useGetServiceRequestTransactionByReferenceForCustomerQuery,
	useGetRefundTransactionByReferenceForCustomerQuery,
	useLazyGetUtilityDashboardSummaryQuery,
	useLazyGetAggregatedTransactionsQuery,
	useLazyGetDistrictRevenueQuery,
  } = transactionEndpoint;
